/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Table Column 공통 클래스를 정의한다.
 *
 * @file /src/models/table/Column.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 10.
 */
import * as menus from '@components/menu/elements/Items';
import * as filters from './Filters';
import { Table } from './Table';
import { Field } from '@components/form/field/Field';
import { Record } from '@models/store/Record';
import { Summarizer } from './Summary';
import { Row } from '@models/table/Row';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { LanguageText } from '@utils/Language';
import './Column.css';

type Renderer<T> = (value: any, record: Record, $dom: Dom, column: Column, table: T) => string | Promise<string>;
type Editor<T> = (value: any, record: Record, column: Column, table: T) => Field | Promise<Field>;

export interface Properties<T> {
    /**
     * 컬럼제목
     */
    text?: LanguageText;

    /**
     * 데이터를 가져올 인덱스명
     */
    dataIndex?: string;

    /**
     * 컬럼 너비
     */
    width?: number;

    /**
     * 컬럼 최소 너비
     */
    minWidth?: number;

    /**
     * 컬럼 길이
     */
    length?: number;

    /**
     * 컬럼 최소 길이
     */
    minLength?: number;

    /**
     * 컬럼 숨김 여부
     */
    hidden?: boolean;

    /**
     * FLEX
     */
    flex?: boolean | number;

    /**
     * 컬럼제목 가로정렬
     */
    headerAlign?: 'left' | 'center' | 'right';

    /**
     * 컬럼제목 세로정렬
     */
    headerVerticalAlign?: 'top' | 'middle' | 'bottom';

    /**
     * 컬럼 가로정렬
     */
    textAlign?: 'left' | 'center' | 'right';

    /**
     * 컬럼 세로정렬
     */
    textVerticalAlign?: 'top' | 'middle' | 'bottom';

    /**
     * 컬럼 커스텀 클래스
     */
    textClass?: string;

    /**
     * 자식 컬럼
     */
    columns?: Properties<T>[];

    /**
     * 필터 설정
     */
    filter?: filters.Properties;

    /**
     * 컬럼 랜더러
     */
    renderer?: Renderer<T>;

    /**
     * 컬럼 에디터
     */
    editor?: Editor<T>;

    /**
     * 대체행 컬럼
     */
    alternative?: {
        text?: LanguageText;
        dataIndex?: string;
        sortable?: boolean | 'ASC' | 'DESC';
        filter?: filters.Properties;
        menus?: menus.Properties[];
        header?: boolean;
        renderer?: Renderer<T>;
    };

    /**
     * 합계설정
     */
    summary?: Summarizer<T> | string;

    /**
     * 컬럼 정렬가능 여부 @todo 함수추가
     */
    sortable?: boolean | 'ASC' | 'DESC';

    /**
     * 리사이즈 여부
     */
    resizable?: boolean;

    /**
     * 행 선택가능 여부
     */
    selectable?: boolean;

    /**
     * 컬럼 메뉴 목록
     */
    menus?: menus.Properties[];
}

export class Column {
    /**
     * 테이블객체
     */
    table: Table;

    /**
     * 컬럼제목
     */
    text: LanguageText | null = null;

    /**
     * 데이터를 가져올 인덱스명
     */
    dataIndex: string | null = null;

    /**
     * 컬럼 너비
     */
    width: number | null = null;

    /**
     * 컬럼 최소 너비
     */
    minWidth: number | null = null;

    /**
     * 컬럼 길이
     */
    length: number | null = null;

    /**
     * 컬럼 최소 길이
     */
    minLength: number | null = null;

    /**
     * 컬럼 숨김 여부
     */
    hidden: boolean = false;

    /**
     * FLEX
     */
    flex: number;

    /**
     * 컬럼제목 가로정렬
     */
    headerAlign: 'left' | 'center' | 'right' | null = null;

    /**
     * 컬럼제목 세로정렬
     */
    headerVerticalAlign: 'top' | 'middle' | 'bottom' | null = null;

    /**
     * 컬럼 가로정렬
     */
    textAlign: 'left' | 'center' | 'right' | null = null;

    /**
     * 컬럼 세로정렬
     */
    textVerticalAlign: 'top' | 'middle' | 'bottom' | null = null;

    /**
     * 컬럼 커스텀 클래스
     */
    textClass: string | null = null;

    /**
     * 컬럼 인덱스
     */
    columnIndex: number | null = null;

    /**
     * 자식 컬럼
     */
    columns: Column[] = [];

    /**
     * 고정 여부
     */
    freeze = false;

    /**
     * 컬럼 랜더러
     */
    renderer: Renderer<Table> = null;

    /**
     * 컬럼 에디터
     */
    editor: Editor<Table>;

    /**
     * 대체행 컬럼
     */
    alternative: {
        text: LanguageText;
        dataIndex: string;
        sortable: boolean | 'ASC' | 'DESC';
        header: boolean;
        renderer: Renderer<Table>;
    } | null = null;

    /**
     * 합계설정
     */
    summary: Summarizer<Table> | null = null;

    /**
     * 컬럼 정렬가능 여부
     */
    sortable: boolean | 'ASC' | 'DESC' = false;

    /**
     * 리사이즈 여부
     */
    resizable: boolean = false;

    /**
     * 행 선택가능 여부
     */
    selectable: boolean = true;

    /**
     * 부모 컬럼
     */
    parent: Column = null;

    /**
     * 편집중인 필드
     */
    editingField: Field = null;

    /**
     * 컬럼 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(
        public properties: Properties<Table>,
        table: Table
    ) {
        this.table = table;
        this.text = this.properties.text ?? null;
        this.dataIndex = this.properties.dataIndex ?? null;
        this.width = this.properties.width ?? null;
        this.minWidth = this.properties.minWidth ?? null;
        this.length = this.properties.length ?? null;
        this.minLength = this.properties.minLength ?? null;
        this.hidden = this.properties?.hidden === true;
        this.properties.flex ??= false;
        if (typeof this.properties.flex == 'boolean') {
            this.flex = this.properties.flex === true ? 1 : 0;
        } else {
            this.flex = this.properties.flex;
        }
        this.headerAlign = this.properties.headerAlign ?? 'left';
        this.headerVerticalAlign = this.properties.headerVerticalAlign ?? 'middle';
        this.textAlign = this.properties.textAlign ?? 'left';
        this.textVerticalAlign = this.properties.textVerticalAlign ?? 'middle';
        this.textClass = this.properties.textClass ?? null;
        this.renderer = this.properties.renderer ?? null;
        this.editor = this.properties.editor ?? null;

        this.summary = null;
        if (typeof this.properties.summary == 'string') {
            this.summary = { type: this.properties.summary as Summarizer<Table>['type'] };
        } else if (this.properties.summary) {
            this.summary = this.properties.summary as Summarizer<Table>;
        }

        if (this.summary !== null) {
            this.summary.dataIndex ??= this.dataIndex;
            this.summary.textAlign ??= 'right';
            this.summary.textClass ??= null;
            this.summary.renderer ??= null;
        }

        this.sortable = this.properties?.sortable ?? false;
        this.resizable = this.properties?.resizable !== false;
        this.selectable = this.properties?.selectable !== false;

        const alternative = this.properties.alternative ?? null;
        if (alternative !== null) {
            this.alternative = {
                text: alternative.text ?? null,
                dataIndex: alternative.dataIndex ?? null,
                sortable: alternative.sortable ?? false,
                header: alternative.header !== false,
                renderer: (alternative.renderer as Renderer<Table>) ?? null,
            };
        }

        this.initColumns(this.properties.columns ?? []);
    }

    /**
     * 하위 컬럼을 세팅한다.
     *
     * @param columns - 하위 컬럼 목록
     */
    initColumns(columns: Properties<Table>[]): void {
        for (const item of columns ?? []) {
            const column = new Column(item, this.table);
            if (column.isHidden() == true) continue;

            column.setParent(this);
            this.columns.push(column);
        }
    }

    /**
     * 테이블을 가져온다.
     *
     * @returns 테이블
     */
    getTable(): Table {
        return this.table;
    }

    /**
     * 상위 컬럼을 세팅한다.
     *
     * @param parent - 상위 컬럼
     */
    setParent(parent: Column): void {
        this.parent = parent;
    }

    /**
     * 상위 컬럼을 가져온다.
     *
     * @returns 상위 컬럼
     */
    getParent(): Column | null {
        return this.parent;
    }

    /**
     * Row 내 컬럼을 가져온다.
     *
     * @param record - row 데이터
     * @param columnIndex - column 인덱스
     * @param row - row
     * @returns Row 내 컬럼
     */
    $getColumn(record: Record, columnIndex: number, row: Row): Dom {
        const $column = Html.create('div', {
            'data-model': 'table',
            'data-role': 'column',
            'data-index': columnIndex.toString(),
        });

        if (this.editor !== null) {
            $column.setStyle(
                '--aui-column-anchor-name',
                '--aui-component-' + this.getTable().getId() + '-column-' + row.getRecord().getId() + '-' + columnIndex
            );
            $column.addClass('editable');
        }

        /**
         * 트리일경우, 확장버튼 및 들여쓰기를 추가한다.
         */
        if (row.getTable().isTree() == true && columnIndex == 0) {
            $column.addClass('leaf');
            $column.html('<i class="depth"></i>'.repeat(record.getDepth()));
            const $toggle = Html.create('i', { 'class': 'toggle' });
            const $button = Html.create('button', { type: 'button' });
            $button.on('pointerdown', async (_, e) => {
                e.preventDefault();
                e.stopImmediatePropagation();

                if (record.hasChildren() == true) {
                    await row.toggle();
                }
            });
            $button.on('click', async (_, e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
            });
            $button.on('dblclick', async (_, e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
            });
            $toggle.append($button);
            $column.append($toggle);
        }

        if (this.alternative !== null || $column.hasClass('leaf')) {
            const $view = Html.create('div', { 'data-model': 'table', 'data-view': 'view' });
            $view.addClass(this.textAlign).addClass(this.textVerticalAlign);
            if (this.textClass !== null) {
                $view.addClass(...this.textClass.split(' '));
            }
            $column.append($view);

            if (this.alternative !== null) {
                const $alterView = Html.create('div', { 'data-model': 'table', 'data-view': 'alternative' });
                $alterView.addClass(this.textAlign).addClass(this.textVerticalAlign);
                if (this.textClass !== null) {
                    $alterView.addClass(...this.textClass.split(' '));
                }
                $column.append($alterView);
            }
        } else {
            $column.setAttr('data-view', 'view');
            $column.addClass(this.textAlign).addClass(this.textVerticalAlign);
            if (this.textClass !== null) {
                $column.addClass(...this.textClass.split(' '));
            }
        }

        this.#setValue(record, $column);

        return $column;
    }

    /**
     * 하위 컬럼이 있는지 여부를 가져온다.
     *
     * @returns 하위 컬럼 여부
     */
    hasColumns(): boolean {
        return this.columns.length > 0;
    }

    /**
     * 하위 컬럼을 가져온다.
     *
     * @returns 하위 컬럼
     */
    getColumns(): Column[] {
        return this.columns;
    }

    /**
     * 하위 모든 컬럼을 직렬화한다.
     *
     * @returns 직렬화된 하위 컬럼들
     */
    toPlain(): Column[] {
        if (this.columns.length == 0) {
            return [this];
        }

        const columns = [];
        for (const column of this.columns) {
            columns.push(...column.toPlain());
        }

        return columns;
    }

    /**
     * 컬럼 인덱스를 설정한다.
     *
     * @param index - 컬럼 인덱스
     */
    setIndex(index: number): void {
        this.columnIndex = index;
    }

    /**
     * 컬럼이 고정된 너비를 가지고 있을 경우, 해당 너비를 가져온다.
     *
     * @returns 고정너비
     */
    getWidth(): string {
        return this.length !== null ? `${this.length}rem` : `${this.width ?? 50}px`;
    }

    /**
     * 컬럼이 가변너비일 경우 최소 너비를 가져온다.
     *
     * @returns 최소너비
     */
    getMinWidth(): string {
        return this.minLength !== null ? `${this.minLength}rem` : `${this.minWidth ?? 50}px`;
    }

    /**
     * 컬럼너비 픽셀단위로 설정한다.
     *
     * @param width - 변경할 너비
     */
    setWidth(width: number): void {
        this.flex = 0;
        this.width = width;
        this.minWidth = null;
        this.length = null;

        if (this.getParent() !== null) {
            this.getTable()?.getHeader()?.getHead(this.getParent())?.updateHeader();
        }
        this.getTable().updateFreeze();
    }

    /**
     * 묶음 컬럼의 Flex-Grow 값을 계산하여 가져온다.
     *
     * @returns flexGrow
     */
    getChildrenFlexGrow(): number {
        if (this.getColumns().length == 0) {
            return this.flex;
        } else {
            let flexGrow = 0;
            for (const column of this.getColumns()) {
                flexGrow += column.getChildrenFlexGrow();
            }
            return flexGrow;
        }
    }

    /**
     * 묶음 컬럼의 Flex-Basis 값을 계산하여 가져온다.
     *
     * @returns flexBasis
     */
    getChildrenFlexBasis(): { px: number; rem: number } {
        let px: number = 0;
        let rem: number = 0;
        if (this.hasColumns() == false) {
            const width = this.flex === 0 ? this.getWidth() : this.getMinWidth();
            if (width.endsWith('px') == true) {
                px = parseInt(width.replace(/px$/, ''), 10);
            } else {
                rem = parseInt(width.replace(/rem$/, ''), 10);
            }
        } else {
            for (const column of this.columns) {
                const basis = column.getChildrenFlexBasis();
                px += basis.px;
                rem += basis.rem;
            }
            px += this.columns.length - 1;
        }

        return { px: px, rem: rem };
    }

    /**
     * 컬럼 숨김 여부를 가져온다.
     *
     * @returns 숨김여부
     */
    isHidden(): boolean {
        return this.hidden;
    }

    /**
     * 컬럼 숨김 여부를 설정한다.
     *
     * @param hidden - 숨김여부
     */
    setHidden(hidden: boolean): void {
        this.hidden = hidden;
        this.getTable().updateFreeze();

        if (this.getTable().isRendered() == true) {
            this.getTable().getBody().render();
        }
    }

    /**
     * 컬럼 클릭시, 행 선택이 가능한지 여부를 가져온다.
     *
     * @returns 행 선택가능여부
     */
    isSelectable(): boolean {
        return this.selectable == true && this.editor === null;
    }

    /**
     * 컬럼이 편집가능한지 여부를 가져온다.
     *
     * @returns 편집가능여부
     */
    isEditable(): boolean {
        return this.editor !== null;
    }

    /**
     * 컬럼을 고정시킨다.
     *
     * @param freeze - 고정 여부
     */
    setFreeze(freeze: boolean): void {
        this.freeze = freeze;

        if (this.hasColumns() == true) {
            this.getColumns().forEach((column: Column) => {
                column.setFreeze(freeze);
            });
        }
    }

    /**
     * 컬럼너비를 업데이트한다.
     */
    updateWidth(): void {
        this.getTable()
            .$getContent()
            .setStyleProperty(
                `--column-${this.columnIndex}-width`,
                this.flex > 0 ? this.getMinWidth() : this.getWidth()
            );

        this.getTable()
            .$getContent()
            .setStyleProperty(
                `--column-${this.columnIndex}-flex`,
                this.flex > 0 ? `${this.flex} 0 ${this.getMinWidth()}` : `0 0 auto`
            );
    }

    /**
     * 컬럼을 업데이트한다.
     *
     * @param record - row 데이터
     * @param columnIndex - column 인덱스
     * @param row - row
     * @returns 업데이트된 컬럼
     */
    update(record: Record, columnIndex: number, row: Row): Dom {
        const $column = row.$columns[columnIndex] ?? Html.get('div[data-index="' + columnIndex + '"]', row.$getRow());
        if ($column !== null) {
            this.#setValue(record, $column, true);
        }

        return $column;
    }

    /**
     * 변경된 레코드 필드명을 배열로 받아, 현재 컬럼이 변경될 필요가 있는지 확인한다.
     *
     * @param fields - 변경된 필드목록
     * @returns 업데이트필요여부
     */
    isUpdateNeeded(fields: string[]): boolean {
        /**
         * 렌더러가 존재할 경우 현재 컬럼의 dataIndex 와 무관하게 변경필요가 있다.
         */
        if (this.renderer !== null || (this.alternative?.renderer ?? null) !== null) {
            return true;
        }

        if (fields.indexOf(this.dataIndex) > -1) {
            return true;
        }

        if (this.alternative !== null && fields.indexOf(this.alternative.dataIndex) > -1) {
            return true;
        }

        return false;
    }

    /**
     * 컬럼이 편집가능한 경우 편집모드로 진입한다.
     *
     * @param row - 행
     * @param $column - 컬럼 DOM
     */
    async onEdit(row: Row, $column: Dom): Promise<void> {
        const record = row.getRecord();
        const value = record.get(this.dataIndex);

        const anchorName = `${record.getId()}-${$column.getAttr('data-index')}`;

        if ($column.getAttr('data-popover') != anchorName) {
            $column.setAttr('data-popover', anchorName);
            $column.setStyle('anchor-name', `--anchor-${anchorName}`);
        }

        this.editingField = await this.editor(value, record, this, this.getTable());
        if (this.editingField !== null) {
            this.editingField.addEvent('render', async (me) => {
                await me.setValue(value);
                me.focus();
            });

            this.editingField.addEvent('keyboard', async (e, field) => {
                /**
                 * 에디터폼에서 방향키를 입력했을 때, 그리드의 셀 포커스 위치를 변경하지 않도록 차단
                 * 에디터폼에서 스페이스를 입력했을 때, 그리드의 로우가 선택되지 않도록 차단
                 */
                if (e.key.startsWith('Arrow') == true || e.key == ' ') {
                    e.stopPropagation();
                }

                /**
                 * 에디터 필드가 Input 인 경우, Enter, Esc 키 입력시 편집을 종료한다.
                 * Enter 키는 입력사항을 적용하고, Esc 키는 입력사항을 무시한다.
                 */
                if (field.variant == 'input') {
                    if (e.key == 'Enter' || e.key == 'Escape') {
                        e.stopPropagation();
                        await this.getTable()
                            .getBody()
                            .onEdited(e.key == 'Enter');

                        this.editingField.remove();
                        this.editingField = null;
                    }
                }
            });

            this.editingField
                .$getComponent()
                .setAttr('popover', 'manual')
                .setStyle('position-anchor', `--anchor-${anchorName}`);

            this.getTable().$getComponent().append(this.editingField.$getComponent());

            this.editingField.$getComponent().getEl().showPopover();
            await this.editingField.render();

            this.editingField.$getComponent().on('toggle', async (_, e: ToggleEvent) => {
                if (e.newState === 'closed') {
                    await this.getTable().getBody().onEdited(true);
                    this.editingField.remove();
                    this.editingField = null;
                }
            });
        }
    }

    /**
     * 객체를 제거한다.
     */
    destroy(): void {
        this.columns.forEach((column) => {
            column.destroy();
        });
        this.columns = [];

        this.table = null;
        this.editingField?.remove();
        this.editingField = null;
    }

    /**
     * 컬럼 Dom 내부 내용을 세팅한다.
     *
     * @param record - 레코드
     * @param $column - 컬럼 Dom
     * @param is_update - 업데이트 여부 (컬럼이 렌더링 된 이후 데이터가 변경될 경우 속도를 위해 DOM 을 비교한다.)
     */
    async #setValue(record: Record, $column: Dom, is_update: boolean = false): Promise<void> {
        if (this.dataIndex !== null) {
            if (record.isUpdated(this.dataIndex) == true) {
                $column.addClass('updated');
            } else {
                $column.removeClass('updated');
            }
        }

        const $view = $column.getAttr('data-view') !== '' ? $column : Html.get('> div[data-view="view"]', $column);
        this.#renderView($view, record, this.dataIndex, this.renderer, $column, is_update);

        if (this.alternative !== null) {
            const $view = Html.get('> div[data-view="alternative"]', $column);
            this.#renderView($view, record, this.alternative.dataIndex, this.alternative.renderer, $column, is_update);
        }
    }

    /**
     * 컬럼 뷰에 값을 렌더링한다.
     *
     * @param $view - 렌더링할 Dom
     * @param record - 레코드
     * @param dataIndex - 데이터인덱스
     * @param renderer - 렌더러
     * @param $column - 컬럼 Dom
     * @param is_update - 업데이트 여부
     */
    #renderView(
        $view: Dom,
        record: Record,
        dataIndex: string,
        renderer: Renderer<Table> | null,
        $column: Dom,
        is_update: boolean
    ): void {
        const value = dataIndex ? record.get(dataIndex) : '';
        if (renderer !== null) {
            const text = renderer(value, record, $column, this, this.getTable());
            if (typeof text == 'object' && typeof text.then === 'function') {
                text.then((text) => {
                    $view.html(text?.toString() ?? '', is_update);
                });
            } else {
                $view.html(text?.toString() ?? '', is_update);
            }
        } else {
            $view.html(value !== null && value !== undefined ? value.toString() : '', is_update);
        }
    }
}
